import { useEffect, useState } from "react";
import { Search, Loader2, BookOpen, Plus } from "lucide-react";
import api, { FIELDS, LANGUAGES, formatApiError } from "@/api";
import { useLang } from "@/context/LanguageContext";
import LibraryTutorial from "@/components/LibraryTutorial";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1553880287-f6d6720a05a9?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function Library() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [field, setField] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showContribute, setShowContribute] = useState(false);

  const runSearch = async (query = q, fld = field) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (fld) params.set("field", fld);
      const { data } = await api.get(`/catalog/search?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Search failed.");
    } finally { setLoading(false); }
  };

  useEffect(() => { runSearch("", null); }, []);
  useEffect(() => { runSearch(q, field); /* eslint-disable-next-line */ }, [field]);

  return (
    <div>
      <LibraryTutorial />
      {/* Hero */}
      <section className="border-b border-[#DCDCDC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">{t("lib.eyebrow")}</div>
            <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
              {t("lib.title")}
            </h1>
            <p className="font-body text-lg text-[#3A3A3A] mt-6 max-w-2xl leading-relaxed">
              {t("lib.intro")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#DCDCDC]">
              <img src={HERO_IMG} alt="Archive shelves" className="w-full h-[320px] object-cover grayscale" />
            </div>
          </div>
        </div>
      </section>

      {/* Search + Filters */}
      <section className="border-b border-[#DCDCDC] bg-white sticky top-[73px] z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6">
          <form onSubmit={(e) => { e.preventDefault(); runSearch(); }} className="flex items-center gap-3">
            <div className="flex items-center flex-1 border border-[#111111] px-4">
              <Search size={18} className="text-[#6C6C6C]" />
              <input
                data-testid="catalog-search-input"
                type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("lib.searchPh")}
                className="w-full py-3 px-3 outline-none font-body text-base bg-transparent"
              />
            </div>
            <button type="submit" className="btn-primary" data-testid="catalog-search-btn">{t("lib.searchBtn")}</button>
            <button type="button" onClick={() => setShowContribute(true)} className="btn-outline hidden md:inline-flex items-center gap-2" data-testid="open-contribute-btn">
              <Plus size={14} /> {t("lib.contribute")}
            </button>
          </form>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="field-filters">
            <button className={`chip ${field === null ? "active" : ""}`} onClick={() => setField(null)} data-testid="filter-all">{t("lib.allFields")}</button>
            {FIELDS.map((f, idx) => (
              <button key={f.value} className={`chip ${field === f.value ? "active" : ""}`}
                onClick={() => setField(f.value)} data-testid={`filter-${f.value}`}>
                {t(`p180.field${idx + 1}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Catalog Grid */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          {loading ? (
            <div className="flex items-center gap-3 text-[#6C6C6C] font-body">
              <Loader2 className="animate-spin" size={18} /> Fetching catalog…
            </div>
          ) : items.length === 0 ? (
            <div className="border border-[#DCDCDC] p-10 text-center">
              <div className="font-headline text-2xl">No results.</div>
              <p className="font-body text-[#6C6C6C] mt-2">Try another search or explore a different field.</p>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-8">
                <div className="eyebrow" data-testid="results-count">{items.length} items in the catalog</div>
                <div className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">
                  Gutendex · Open Library · Contributed
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#DCDCDC]" data-testid="catalog-grid">
                {items.map((it, i) => <CatalogCard key={it.id + i} item={it} />)}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Contribute Modal */}
      {showContribute && <ContributeModal onClose={() => setShowContribute(false)} onSubmitted={() => { setShowContribute(false); runSearch(); }} />}

      {/* Mobile floating contribute */}
      <button onClick={() => setShowContribute(true)} className="btn-primary fixed bottom-6 right-6 md:hidden z-40" data-testid="mobile-contribute-btn">
        Contribute
      </button>
    </div>
  );
}

function CatalogCard({ item }) {
  const { t } = useLang();
  const fieldLabelMap = {
    "education-youth": t("p180.field1"),
    "public-health-care": t("p180.field2"),
    "environmental-sustainability": t("p180.field3"),
    "arts-culture-humanities": t("p180.field4"),
    "economy-labor": t("p180.field5"),
  };
  const fieldLabel = fieldLabelMap[item.field] || item.field;
  return (
    <a
      href={item.read_url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="border-r border-b border-[#DCDCDC] p-6 md:p-8 group flex flex-col justify-between min-h-[280px]"
      data-testid="catalog-card"
    >
      <div>
        <div className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">{item.call_number}</div>
        <h3 className="font-headline text-xl md:text-2xl mt-4 leading-tight group-hover:underline decoration-1 underline-offset-4">
          {item.title || "Untitled"}
        </h3>
        <div className="font-body text-[#3A3A3A] mt-2">{item.author || "Unknown"}</div>
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="font-ui text-[9px] tracking-[0.25em] uppercase text-[#6C6C6C] border border-[#DCDCDC] px-2 py-1">
            {fieldLabel}
          </span>
          <span className="font-ui text-[9px] tracking-[0.25em] uppercase text-[#111111] inline-flex items-center gap-1">
            <BookOpen size={12} /> {t("lib.readFree")}
          </span>
        </div>
        <div className="flex items-center justify-between text-[9px] tracking-[0.22em] uppercase font-ui text-[#6C6C6C]">
          <span data-testid="license-tag">{item.license || "OPEN ACCESS"}</span>
          <span>{item.source}{item.year ? ` · ${item.year}` : ""}</span>
        </div>
      </div>
    </a>
  );
}

function ContributeModal({ onClose, onSubmitted }) {
  const [form, setForm] = useState({
    title: "", author_name: "", field: "",
    abstract: "", language: "English", external_link: "",
    submitter_email: "", rights_consent: false, file_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null); // { url, original_filename, size }
  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedFile(data);
      setForm((f) => ({ ...f, file_url: data.url }));
      toast.success("File uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const clearFile = () => { setUploadedFile(null); setForm((f) => ({ ...f, file_url: "" })); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rights_consent) { toast.error("Please confirm rights & consent."); return; }
    setSaving(true);
    try {
      await api.post("/contributions", form);
      toast.success("Thank you — your contribution is under review.");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose} data-testid="contribute-modal">
      <div className="bg-white max-w-3xl w-full border border-[#111111] my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#DCDCDC]">
          <div>
            <div className="eyebrow">Contribute to the Archive</div>
            <div className="font-headline text-2xl mt-1">Submit a text, paper, or oral history</div>
          </div>
          <button onClick={onClose} className="font-ui text-xs uppercase tracking-[0.2em] text-[#6C6C6C]" data-testid="contribute-close">Close</button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contribute-form">
          <label className="block md:col-span-2">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Title</span>
            <input required className="editorial-input" data-testid="c-title" value={form.title} onChange={(e) => onChange("title", e.target.value)} />
          </label>
          <label className="block">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Author Name</span>
            <input required className="editorial-input" data-testid="c-author" value={form.author_name} onChange={(e) => onChange("author_name", e.target.value)} />
          </label>
          <label className="block">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Research Field (your own words)</span>
            <input required className="editorial-input" data-testid="c-field" placeholder="e.g. Migration Studies, Ethnomusicology…"
              value={form.field} onChange={(e) => onChange("field", e.target.value)} />
          </label>
          <label className="block md:col-span-2">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Abstract</span>
            <textarea required rows={4} className="editorial-input" data-testid="c-abstract" value={form.abstract} onChange={(e) => onChange("abstract", e.target.value)} />
          </label>
          <label className="block">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Language</span>
            <select className="editorial-input" data-testid="c-language" value={form.language} onChange={(e) => onChange("language", e.target.value)}>
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Your Email</span>
            <input required type="email" className="editorial-input" data-testid="c-email" value={form.submitter_email} onChange={(e) => onChange("submitter_email", e.target.value)} />
          </label>
          <label className="block md:col-span-2">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">External Link (PDF, DOI, or URL)</span>
            <input type="url" className="editorial-input" data-testid="c-link" placeholder="https://…" value={form.external_link} onChange={(e) => onChange("external_link", e.target.value)} />
          </label>
          <div className="block md:col-span-2">
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">
              Or Upload a File (PDF, EPUB, DOCX, TXT, image, audio · max 25 MB)
            </span>
            {uploadedFile ? (
              <div className="border border-[#111111] p-4 flex items-center justify-between" data-testid="uploaded-file">
                <div className="min-w-0">
                  <div className="font-body text-[#111111] truncate">{uploadedFile.original_filename}</div>
                  <div className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#6C6C6C] mt-1">
                    {Math.max(1, Math.round((uploadedFile.size || 0) / 1024))} KB · Stored
                  </div>
                </div>
                <button type="button" onClick={clearFile} className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#111111] underline underline-offset-4 shrink-0 ml-4" data-testid="c-remove-file">Remove</button>
              </div>
            ) : (
              <label className="border border-dashed border-[#111111] p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-[#F5F5F5] transition-colors" data-testid="c-file-drop">
                <input type="file" className="hidden" onChange={handleFile}
                  accept=".pdf,.epub,.txt,.docx,.rtf,.odt,.md,.jpg,.jpeg,.png,.webp,.mp3,.m4a,.wav"
                  data-testid="c-file-input" disabled={uploading} />
                <span className="font-ui text-[11px] tracking-[0.2em] uppercase font-semibold text-[#111111]">
                  {uploading ? "Uploading…" : "Choose File"}
                </span>
                <span className="font-body text-sm text-[#6C6C6C]">or provide an external link above</span>
              </label>
            )}
          </div>
          <label className="flex items-start gap-3 md:col-span-2 cursor-pointer">
            <input type="checkbox" data-testid="c-consent" checked={form.rights_consent} onChange={(e) => onChange("rights_consent", e.target.checked)}
              className="mt-1 h-4 w-4 accent-black" />
            <span className="font-body text-[#3A3A3A] text-sm">
              I confirm I hold the rights to submit this work, or that it is in the public domain, and I consent to its inclusion in the archive under review.
            </span>
          </label>
          <div className="md:col-span-2 pt-2 flex gap-3">
            <button disabled={saving} type="submit" className="btn-primary" data-testid="c-submit">
              {saving ? "Submitting…" : "Submit for Review"}
            </button>
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
